// ============================================================================
// CrewAhead Maritime Platform - Career Intelligence API Functions
// ============================================================================
// Sea-to-shore career transition endpoints: readiness scores, career paths,
// skill gaps, and course recommendations.

import { apiClient, handleApiError } from './client';
import { API_ENDPOINTS } from '../utils/constants';
import type {
  CareerTransitionProfile,
  ReadinessScore,
  CareerPath,
  SkillGapResult,
  CourseRecommendation,
  CareerBarrierResult,
  ShoreJobMatch,
  SeaToShoreSummary,
  SeaToShorePathResult,
  ShoreToSeaPathResult,
  RecommendationCareerPath,
  TargetRoleMatrix,
  PromotionReadiness,
} from '../types';

// ─── Transition Profile ───────────────────────────────────────────────────────

/**
 * GET /career-transition/profile/
 *
 * Fetches the current user's career transition profile.
 * Returns 404 if the profile has not been created yet.
 */
export async function getCareerTransitionProfile(): Promise<CareerTransitionProfile> {
  try {
    const response = await apiClient.get<CareerTransitionProfile>(
      API_ENDPOINTS.CAREER_TRANSITION.PROFILE
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /career-transition/profile/
 *
 * Creates a new career transition profile for the current user.
 */
export async function createCareerTransitionProfile(
  data: Partial<CareerTransitionProfile>
): Promise<CareerTransitionProfile> {
  try {
    const response = await apiClient.post<CareerTransitionProfile>(
      API_ENDPOINTS.CAREER_TRANSITION.PROFILE,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * PATCH /career-transition/profile/
 *
 * Partially updates the current user's career transition profile.
 */
export async function updateCareerTransitionProfile(
  data: Partial<CareerTransitionProfile>
): Promise<CareerTransitionProfile> {
  try {
    const response = await apiClient.patch<CareerTransitionProfile>(
      API_ENDPOINTS.CAREER_TRANSITION.PROFILE,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * PATCH /career-transition/target-positions/
 *
 * Updates target shore positions and refreshes career intelligence.
 */
export async function updateCareerTargetPositions(
  targetPositionIds: number[]
): Promise<CareerTransitionProfile> {
  try {
    const response = await apiClient.patch<CareerTransitionProfile>(
      API_ENDPOINTS.CAREER_TRANSITION.TARGET_POSITIONS,
      { targetShorePositions: targetPositionIds }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /career-transition/activate/
 *
 * Activates the transition profile with the given target positions.
 *
 * @param targetPositionIds - Array of shore position IDs to target
 */
export async function activateTransitionProfile(
  targetPositionIds: number[]
): Promise<CareerTransitionProfile> {
  try {
    const response = await apiClient.post<CareerTransitionProfile>(
      API_ENDPOINTS.CAREER_TRANSITION.ACTIVATE,
      { targetShorePositions: targetPositionIds }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /career-transition/deactivate/
 *
 * Deactivates the career transition profile (soft-off, data is preserved).
 */
export async function deactivateTransitionProfile(): Promise<void> {
  try {
    await apiClient.post(API_ENDPOINTS.CAREER_TRANSITION.DEACTIVATE);
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Intelligence Endpoints ───────────────────────────────────────────────────

/**
 * GET /career-transition/summary/
 *
 * Returns a condensed dashboard summary for the career transition panel.
 */
export async function getCareerSummary(): Promise<SeaToShoreSummary> {
  try {
    const response = await apiClient.get<SeaToShoreSummary>(
      API_ENDPOINTS.CAREER_TRANSITION.SUMMARY
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /career-transition/readiness/?target_position_id={id}
 *
 * Returns a readiness score showing how prepared the current user is
 * to transition to a target shore position.
 *
 * @param targetPositionId - Optional position ID to scope the score
 */
export async function getReadinessScore(
  targetPositionId?: number
): Promise<ReadinessScore> {
  try {
    const query = new URLSearchParams();
    if (targetPositionId !== undefined) {
      query.set('target_position_id', String(targetPositionId));
    }

    const url = `${API_ENDPOINTS.CAREER_TRANSITION.READINESS}${query.toString() ? `?${query.toString()}` : ''}`;
    const response = await apiClient.get<ReadinessScore>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /career-transition/career-path/?target_position_id={id}
 *
 * Returns a step-by-step career path from the user's current sea rank
 * to the specified shore position.
 *
 * @param targetPositionId - Optional position ID to scope the path
 */
export async function getCareerPath(
  targetPositionId?: number
): Promise<CareerPath> {
  try {
    const query = new URLSearchParams();
    if (targetPositionId !== undefined) {
      query.set('target_position_id', String(targetPositionId));
    }

    const url = `${API_ENDPOINTS.CAREER_TRANSITION.CAREER_PATH}${query.toString() ? `?${query.toString()}` : ''}`;
    const response = await apiClient.get<CareerPath>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /career-transition/skill-gaps/?target_position_id={id}
 *
 * Returns a detailed skill gap analysis comparing the user's current
 * competencies against those required for the target position.
 *
 * @param targetPositionId - Optional position ID to scope the analysis
 */
export async function getSkillGaps(
  targetPositionId?: number
): Promise<SkillGapResult> {
  try {
    const query = new URLSearchParams();
    if (targetPositionId !== undefined) {
      query.set('target_position_id', String(targetPositionId));
    }

    const url = `${API_ENDPOINTS.CAREER_TRANSITION.SKILL_GAPS}${query.toString() ? `?${query.toString()}` : ''}`;
    const response = await apiClient.get<SkillGapResult>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /career-transition/recommended-courses/?target_position_id={id}
 *
 * Returns course recommendations that address the user's skill gaps
 * for the target shore position.
 *
 * @param targetPositionId - Optional position ID to scope recommendations
 */
export async function getRecommendedCourses(
  targetPositionId?: number
): Promise<CourseRecommendation[]> {
  try {
    const query = new URLSearchParams();
    if (targetPositionId !== undefined) {
      query.set('target_position_id', String(targetPositionId));
    }

    const url = `${API_ENDPOINTS.CAREER_TRANSITION.RECOMMENDED_COURSES}${query.toString() ? `?${query.toString()}` : ''}`;
    const response = await apiClient.get<CourseRecommendation[]>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /career-transition/shore-jobs/?limit={n}
 *
 * Returns shore-based job postings that match the user's career
 * transition profile.
 *
 * @param limit - Maximum number of results to return
 */
export async function getShoreJobMatches(
  limit?: number
): Promise<ShoreJobMatch[]> {
  try {
    const query = new URLSearchParams();
    if (limit !== undefined) {
      query.set('limit', String(limit));
    }

    const url = `${API_ENDPOINTS.CAREER_TRANSITION.SHORE_JOBS}${query.toString() ? `?${query.toString()}` : ''}`;
    const response = await apiClient.get<ShoreJobMatch[]>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /career-transition/barriers/
 *
 * Returns user-selected and inferred transition barriers for the current user.
 */
export async function getCareerBarriers(): Promise<CareerBarrierResult> {
  try {
    const response = await apiClient.get<CareerBarrierResult>(
      API_ENDPOINTS.CAREER_TRANSITION.BARRIERS
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /career-transition/calculate/
 *
 * Triggers a recalculation of career recommendations.
 * Call after updating profile data to get fresh readiness scores,
 * career paths, and course recommendations.
 */
export async function recalculateRecommendations(): Promise<{
  detail: string;
  recommendations: unknown[];
}> {
  try {
    const response = await apiClient.post<{
      detail: string;
      recommendations: unknown[];
    }>(API_ENDPOINTS.CAREER_TRANSITION.CALCULATE);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Bidirectional Path Engines ───────────────────────────────────────────────

/**
 * GET /career-transition/sea-to-shore-paths/
 *
 * Returns sea-to-shore career path recommendations for the current user,
 * including a gap analysis with missing or expired certificates.
 */
export async function getSeaToShorePaths(): Promise<SeaToShorePathResult> {
  try {
    const response = await apiClient.get<SeaToShorePathResult>(
      API_ENDPOINTS.CAREER_TRANSITION.SEA_TO_SHORE_PATHS
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /career-transition/shore-to-sea-paths/
 *
 * Returns shore-to-sea career path recommendations for the current user,
 * including a gap analysis that highlights expired or missing STCW/Medical
 * certificates required to return to sea.
 */
export async function getShoreToSeaPaths(): Promise<ShoreToSeaPathResult> {
  try {
    const response = await apiClient.get<ShoreToSeaPathResult>(
      API_ENDPOINTS.CAREER_TRANSITION.SHORE_TO_SEA_PATHS
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Archetype-based Recommendation Engine ────────────────────────────────────

/**
 * GET /career-transition/target-roles/
 *
 * Lists all active target role matrices (the canonical shore roles).
 */
export async function getTargetRoles(): Promise<TargetRoleMatrix[]> {
  try {
    const response = await apiClient.get<TargetRoleMatrix[]>(
      API_ENDPOINTS.CAREER_TRANSITION.TARGET_ROLES
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /career-transition/matrix/{roleCode}/
 *
 * Returns the detailed skill gap and course matrix for a single target role.
 */
export async function getRoleMatrix(roleCode: string): Promise<TargetRoleMatrix> {
  try {
    const response = await apiClient.get<TargetRoleMatrix>(
      API_ENDPOINTS.CAREER_TRANSITION.ROLE_MATRIX(roleCode)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export interface CareerPathRuleTarget {
  id: number;
  sourceRank: {
    id: number;
    rankId: string;
    rankName: string;
    level: string;
  };
  targetShorePosition: {
    id: number;
    positionId: string;
    positionName: string;
    level: string;
  };
  pathName: string;
  description: string;
  estimatedTotalMonths: number | null;
  matchScore: number;
  popularityScore: number;
  stepCount: number;
  isActive: boolean;
}

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/**
 * GET /career-transition/career-paths?rank_id={rankId}
 *
 * Browse active CareerPathRule pivots for a given source rank. Used by the
 * seafarer target picker to surface relevance-tiered shore positions.
 */
export async function getCareerPathRulesByRank(
  rankId: number
): Promise<CareerPathRuleTarget[]> {
  try {
    const response = await apiClient.get<PaginatedResponse<CareerPathRuleTarget>>(
      `${API_ENDPOINTS.CAREER_TRANSITION.CAREER_PATHS_BROWSE}?rank_id=${rankId}`
    );
    return response.data.results ?? [];
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /career-transition/generate-path/
 *
 * Generates a custom career path from the user's current rank to a target
 * shore position.
 */
export async function generateCareerPath(
  targetPositionId: number,
  yearsSea?: number
): Promise<RecommendationCareerPath> {
  try {
    const response = await apiClient.post<RecommendationCareerPath>(
      API_ENDPOINTS.CAREER_TRANSITION.GENERATE_PATH,
      {
        target_position_id: targetPositionId,
        years_sea: yearsSea,
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /career-transition/promotion-readiness
 *
 * Returns sea-to-sea promotion readiness for the current user's current rank,
 * including months completed, required, and remaining until exam eligibility.
 * Unsupported ranks return status "not_yet_supported".
 */
export async function getPromotionReadiness(): Promise<PromotionReadiness> {
  try {
    const response = await apiClient.get<PromotionReadiness>(
      API_ENDPOINTS.CAREER_TRANSITION.PROMOTION_READINESS
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}
